// Client-side encryption utility using Web Crypto API
// Uses AES-256-GCM for secure encryption

interface EncryptedData {
  encryptedData: string;
  iv: string;
  authTag: string;
}

/**
 * Converts a base64 string to ArrayBuffer
 */
function base64ToArrayBuffer(base64: string): ArrayBuffer {
  const binaryString = atob(base64);
  const bytes = new Uint8Array(binaryString.length);
  for (let i = 0; i < binaryString.length; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }
  return bytes.buffer;
}

/**
 * Converts ArrayBuffer to base64 string
 */
function arrayBufferToBase64(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

/**
 * Derives a CryptoKey from a base64 encoded key string
 */
async function importKey(base64Key: string): Promise<CryptoKey> {
  const keyData = base64ToArrayBuffer(base64Key);
  return await crypto.subtle.importKey('raw', keyData, { name: 'AES-GCM' }, false, [
    'encrypt',
    'decrypt',
  ]);
}

/**
 * Encrypts data using AES-256-GCM
 * @param data - The data to encrypt (will be JSON stringified)
 * @param base64Key - Base64 encoded 256-bit key
 * @returns Promise<EncryptedData> - Object containing encrypted data, IV, and auth tag
 */
export async function encryptData(data: unknown, base64Key: string): Promise<EncryptedData> {
  try {
    // Convert data to JSON string then to ArrayBuffer
    const jsonString = JSON.stringify(data);
    const encoder = new TextEncoder();
    const dataBuffer = encoder.encode(jsonString);

    // Generate random IV (12 bytes for GCM)
    const iv = crypto.getRandomValues(new Uint8Array(12));

    // Import the key
    const cryptoKey = await importKey(base64Key);

    // Encrypt the data
    const encryptedBuffer = await crypto.subtle.encrypt(
      {
        name: 'AES-GCM',
        iv: iv,
      },
      cryptoKey,
      dataBuffer
    );

    // Extract encrypted data and auth tag
    // In GCM mode, the last 16 bytes are the authentication tag
    const encryptedArray = new Uint8Array(encryptedBuffer);
    const encryptedData = encryptedArray.slice(0, -16);
    const authTag = encryptedArray.slice(-16);

    return {
      encryptedData: arrayBufferToBase64(encryptedData.buffer),
      iv: arrayBufferToBase64(iv.buffer),
      authTag: arrayBufferToBase64(authTag.buffer),
    };
  } catch (error) {
    console.error('Encryption failed:', error);
    throw new Error('Failed to encrypt data');
  }
}

/**
 * Decrypts data using AES-256-GCM (for testing purposes)
 * @param encryptedPayload - The encrypted data payload
 * @param base64Key - Base64 encoded 256-bit key
 * @returns Promise<unknown> - The decrypted data
 */
export async function decryptData(
  encryptedPayload: EncryptedData,
  base64Key: string
): Promise<unknown> {
  try {
    // Convert base64 strings back to ArrayBuffers
    const encryptedData = base64ToArrayBuffer(encryptedPayload.encryptedData);
    const iv = base64ToArrayBuffer(encryptedPayload.iv);
    const authTag = base64ToArrayBuffer(encryptedPayload.authTag);

    // Combine encrypted data and auth tag
    const combined = new Uint8Array(encryptedData.byteLength + authTag.byteLength);
    combined.set(new Uint8Array(encryptedData), 0);
    combined.set(new Uint8Array(authTag), encryptedData.byteLength);

    // Import the key
    const cryptoKey = await importKey(base64Key);

    // Decrypt the data
    const decryptedBuffer = await crypto.subtle.decrypt(
      {
        name: 'AES-GCM',
        iv: new Uint8Array(iv),
      },
      cryptoKey,
      combined
    );

    // Convert back to string and parse JSON
    const decoder = new TextDecoder();
    const jsonString = decoder.decode(decryptedBuffer);
    return JSON.parse(jsonString);
  } catch (error) {
    console.error('Decryption failed:', error);
    throw new Error('Failed to decrypt data');
  }
}

/**
 * Generates a random 256-bit key for AES encryption
 * Use this to generate keys for development/testing
 */
export async function generateEncryptionKey(): Promise<string> {
  const key = crypto.getRandomValues(new Uint8Array(32)); // 256 bits
  return arrayBufferToBase64(key.buffer);
}
